import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import PermissionsEditor from "@/components/shared/PermissionsEditor";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { fetchRbacCatalog, getOrgTypeConfig } from "@/lib/rbac";
import { Plus, Search, Filter, MapPin, Building2, UserCog, ChevronDown, X, RefreshCw, MoreVertical, Eye, Pencil, Trash2, Loader2, Shield, Phone, Mail, User, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { useParams } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [1, 10, 20, 50];
const CRUD_ORDER = ["CREATE", "READ", "UPDATE", "DELETE"];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => typeof item === "string" && item.trim().length > 0);
}

function formatTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function truncateWords(text, maxWords = 8) {
  if (!text && text !== "") return "";
  const words = String(text).split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return words.join(" ");
  return words.slice(0, maxWords).join(" ") + " ...";
}

function normalizeCatalogPermissions(catalog) {
  return (Array.isArray(catalog?.permissions) ? catalog.permissions : [])
    .map((item) => ({
      key: item?.key || "",
      module: item?.module || "",
      action: String(item?.action || "").toUpperCase(),
      label: item?.label || "",
      group: item?.group || "Other",
    }))
    .filter((item) => item.key && item.module && item.action);
}

function extractRoleDefaults(roleEntry) {
  if (typeof roleEntry === "string") return [];
  if (!roleEntry || typeof roleEntry !== "object") return [];
  return toStringArray(
    roleEntry.defaults
    || roleEntry.permissions
    || roleEntry.default_permissions
    || roleEntry.effective
    || roleEntry.granted
    || roleEntry.current,
  );
}

function getRoleKey(roleEntry) {
  if (typeof roleEntry === "string") return roleEntry.trim().toLowerCase();
  if (!roleEntry || typeof roleEntry !== "object") return "";
  return String(roleEntry.role || roleEntry.name || roleEntry.key || roleEntry.id || "")
    .trim()
    .toLowerCase();
}

export default function UsersPage() {
  const { userId: deepLinkedUserId } = useParams();
  const { user, can } = useAuth();
  const canCreateUsers = can("USERS.CREATE");
  const canUpdateUsers = can("USERS.UPDATE");
  const canDeleteUsers = can("USERS.DELETE");
  const canManagePermissions = can("USERS.MANAGE_PERMISSIONS");

  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [orgIdFilter, setOrgIdFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [error, setError] = useState("");
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [createFieldErrors, setCreateFieldErrors] = useState({});

  // ── View user dialog ──────────────────────────────────────────────────────
  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  useEffect(() => {
    if (!deepLinkedUserId) return;
    handleViewUser(deepLinkedUserId);
    // intentionally only reacts to URL user id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkedUserId]);

  const handleViewUser = async (userId) => {
    setViewOpen(true);
    setViewUser(null);
    setViewError("");
    setViewLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setViewUser(data);
    } catch (err) {
      const { message } = extractApiError(err);
      setViewError(message || "Failed to load user details.");
    } finally {
      setViewLoading(false);
    }
  };

  // ── Edit user dialog ──────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editOrgType, setEditOrgType] = useState("");
  const [editOrgName, setEditOrgName] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editOriginalRole, setEditOriginalRole] = useState("");
  const [editSelectedPermissions, setEditSelectedPermissions] = useState([]);
  const [permissionsRefreshKey, setPermissionsRefreshKey] = useState(0);
  const [roleChangeConfirmPending, setRoleChangeConfirmPending] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    role: "",
    is_active: true,
    state: "",
    district: "",
    city: "",
    pincode: "",
    address: "",
    blood_group: "",
    emergency_contact: "",
    password: "",
  });

  const handleEditUser = async (userId) => {
    if (!canUpdateUsers) {
      toast.error("You do not have permission to edit users.");
      return;
    }
    setEditOpen(true);
    setEditUserId(userId);
    setEditError("");
    setEditFieldErrors({});
    setEditLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setEditOrgType(data.org_type || "");
      setEditOrgName(data.org_name || "");
      const userRole = data.role || "";
      setEditOriginalRole(userRole);
      setEditSelectedPermissions([]);
      setEditFormData({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        phone_number: data.phone_number || "",
        role: userRole,
        is_active: data.is_active ?? true,
        state: data.state || "",
        district: data.district || "",
        city: data.city || "",
        pincode: data.pincode || "",
        address: data.address || "",
        blood_group: data.blood_group || "",
        emergency_contact: data.emergency_contact || "",
        password: "",
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to load user.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    setEditFieldErrors((prev) => {
      if (!prev || !prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const handleEditSelectChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    setEditFieldErrors((prev) => {
      if (!prev || !prev[field]) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const handleUpdateUser = async () => {
    if (editFormData.password.trim() && editFormData.password.trim().length < 8) {
      setEditError("Password must be at least 8 characters.");
      return;
    }

    // Check if role is changing
    const roleChanged = editFormData.role !== editOriginalRole;
    if (roleChanged) {
      setRoleChangeConfirmPending(true);
      return;
    }

    // Proceed with submission if role hasn't changed
    await submitUserUpdate();
  };

  const submitUserUpdate = async () => {
    if (!canUpdateUsers) {
      toast.error("You do not have permission to edit users.");
      return;
    }
    setEditSubmitting(true);
    setEditError("");
    const roleChanged = editFormData.role !== editOriginalRole;
    
    try {
      const payload = {
        first_name: editFormData.first_name.trim() || undefined,
        last_name: editFormData.last_name.trim() || undefined,
        phone_number: editFormData.phone_number.trim() || undefined,
        role: editFormData.role || undefined,
        is_active: editFormData.is_active,
        state: editFormData.state.trim() || undefined,
        district: editFormData.district.trim() || undefined,
        city: editFormData.city.trim() || undefined,
        pincode: editFormData.pincode.trim() || undefined,
        address: editFormData.address.trim() || undefined,
        blood_group: editFormData.blood_group || undefined,
        emergency_contact: editFormData.emergency_contact.trim() || undefined,
      };
      if (editFormData.password.trim()) {
        payload.password = editFormData.password.trim();
      }
      await api.patch(`/users/${editUserId}`, payload);
      toast.success("User updated successfully.");
      setEditFieldErrors({});
      
      // If role changed, update refresh key to force PermissionsEditor remount
      if (roleChanged) {
        setPermissionsRefreshKey((prev) => prev + 1);
      }
      
      setEditOpen(false);
      await fetchUsers();
    } catch (err) {
      const { message, details, code } = extractApiError(err);
      if (details?.fields && Array.isArray(details.fields)) {
        const fieldMap = {};
        details.fields.forEach((f) => {
          if (f?.field) fieldMap[f.field] = f.message || f.type || "Invalid value";
        });
        setEditFieldErrors(fieldMap);
      }
      const msg = message || "Failed to update user.";
      setEditError(msg);
    } finally {
      setEditSubmitting(false);
      setRoleChangeConfirmPending(false);
    }
  };

  // ── Delete user dialog ──────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, isActive }
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const handleDeleteUser = async () => {
    if (!canDeleteUsers) {
      toast.error("You do not have permission to delete users.");
      return;
    }
    if (!deleteTarget) return;
    if (deleteTarget.isActive === false) {
      toast.info(`${deleteTarget.name} is already inactive.`);
      setDeleteTarget(null);
      return;
    }

    setDeleteSubmitting(true);
    try {
      await api.patch(`/users/${deleteTarget.id}`, { is_active: false });
      toast.success(`${deleteTarget.name} has been removed.`);
      setDeleteTarget(null);
      await fetchUsers();
    } catch (err) {
      const { message } = extractApiError(err);
      const msg = message || "Failed to remove user.";
      toast.error(msg);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const [formData, setFormData] = useState({
    organization_id: "",
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    phone_number: "",
    password: "",
    role: "super_admin",
    state: "",
    district: "",
    city: "",
    pincode: "",
    address: "",
    blood_group: "",
    emergency_contact: "",
  });

  const [createPermissionsCatalog, setCreatePermissionsCatalog] = useState(null);
  const [createPermissionsLoading, setCreatePermissionsLoading] = useState(false);
  const [createPermissionsError, setCreatePermissionsError] = useState("");
  const [createBaselinePermissions, setCreateBaselinePermissions] = useState([]);
  const [createSelectedPermissions, setCreateSelectedPermissions] = useState([]);

  const roleFilterOptions = useMemo(() => {
    const roles = [...new Set(users.map((u) => u.role).filter(Boolean))];
    return roles.sort();
  }, [users]);

  const selectedOrganization = useMemo(
    () => organizations.find((org) => org.id === formData.organization_id) || null,
    [organizations, formData.organization_id],
  );

  const isPlatformAdmin = user?.org_type === "PLATFORM";
  const callerOrgId = user?.org_id || null;

  const createOrgType = selectedOrganization?.org_type || (!isPlatformAdmin ? user?.org_type : null);

  const addUserOrganizations = useMemo(
    () => organizations.filter((org) => org.org_type !== "CUSTOMER"),
    [organizations],
  );

  const roleOptionsByOrgType = useMemo(
    () => ({
      PLATFORM: ["super_admin", "support_user", "data_analyst", "firmware_engineer"],
      POLICE: ["org_admin", "operator", "dispatcher", "viewer"],
      AMBULANCE: ["org_admin", "operator", "dispatcher", "viewer"],
      FIRE_DEPARTMENT: ["org_admin", "operator", "dispatcher", "viewer"],
      CUSTOMER: ["owner"],
    }),
    [],
  );

  const editRoleOptions = useMemo(() => {
    if (!editOrgType) return ["super_admin", "org_admin", "operator", "owner"];
    return roleOptionsByOrgType[editOrgType] || ["org_admin", "operator"];
  }, [editOrgType, roleOptionsByOrgType]);

  const formRoleOptions = useMemo(() => {
    const orgType = selectedOrganization?.org_type || (!isPlatformAdmin ? user?.org_type : null);
    if (!orgType) return ["super_admin", "org_admin", "operator", "owner"];
    return roleOptionsByOrgType[orgType] || ["org_admin", "operator"];
  }, [isPlatformAdmin, roleOptionsByOrgType, selectedOrganization?.org_type, user?.org_type]);

  const createGroupedPermissions = useMemo(() => {
    const entries = normalizeCatalogPermissions(createPermissionsCatalog);
    const byGroup = new Map();

    entries.forEach((entry) => {
      if (!byGroup.has(entry.group)) {
        byGroup.set(entry.group, {
          groupName: entry.group,
          modules: new Map(),
          actions: new Set(),
        });
      }

      const groupObj = byGroup.get(entry.group);
      groupObj.actions.add(entry.action);

      if (!groupObj.modules.has(entry.module)) {
        groupObj.modules.set(entry.module, {
          module: entry.module,
          map: new Map(),
        });
      }

      groupObj.modules.get(entry.module).map.set(entry.action, entry);
    });

    return [...byGroup.values()].map((groupObj) => {
      const actions = [...groupObj.actions];
      const crudActions = CRUD_ORDER.filter((action) => actions.includes(action));
      const specialActions = actions.filter((action) => !CRUD_ORDER.includes(action));
      return {
        groupName: groupObj.groupName,
        crudActions,
        specialActions,
        modules: [...groupObj.modules.values()].sort((a, b) => a.module.localeCompare(b.module)),
      };
    });
  }, [createPermissionsCatalog]);

  const createOrgPermissionConfig = useMemo(() => {
    const fallback = {
      customizable: false,
      grantable: [],
      roles: [],
      role_defaults: null,
    };

    const orgTypes = Array.isArray(createPermissionsCatalog?.org_types)
      ? createPermissionsCatalog.org_types
      : [];

    if (createOrgType) {
      const base = getOrgTypeConfig(createPermissionsCatalog, createOrgType);
      const rawEntry = orgTypes.find((entry) => entry?.org_type === createOrgType) || null;
      return {
        ...base,
        role_defaults: rawEntry?.role_defaults || rawEntry?.default_permissions_by_role || rawEntry?.role_permissions || rawEntry?.permissions_by_role || null,
      };
    }

    if (!formData.role || !createPermissionsCatalog) return fallback;

    const selectedRole = String(formData.role).trim().toLowerCase();
    const matchingOrg = orgTypes.find((entry) => {
      const roles = Array.isArray(entry?.roles) ? entry.roles : [];
      return roles.some((roleEntry) => getRoleKey(roleEntry) === selectedRole);
    });

    if (!matchingOrg) return fallback;

    return {
      customizable: Boolean(matchingOrg?.customizable),
      grantable: Array.isArray(matchingOrg?.grantable) ? matchingOrg.grantable : [],
      roles: Array.isArray(matchingOrg?.roles) ? matchingOrg.roles : [],
      role_defaults: matchingOrg?.role_defaults || matchingOrg?.default_permissions_by_role || matchingOrg?.role_permissions || matchingOrg?.permissions_by_role || null,
    };
  }, [createOrgType, createPermissionsCatalog, formData.role]);

  const createGrantableSet = useMemo(
    () => new Set(Array.isArray(createOrgPermissionConfig?.grantable) ? createOrgPermissionConfig.grantable : []),
    [createOrgPermissionConfig],
  );

  const createRoleDefaults = useMemo(() => {
    if (!formData.role) return [];

    const selectedRoleKey = String(formData.role).trim().toLowerCase();
    const roles = Array.isArray(createOrgPermissionConfig?.roles) ? createOrgPermissionConfig.roles : [];
    
    // First try to find the role entry and extract defaults
    const roleEntry = roles.find((entry) => getRoleKey(entry) === selectedRoleKey);
    let defaults = extractRoleDefaults(roleEntry);

    // If not found in role entry, try role_defaults map
    if (defaults.length === 0) {
      const roleDefaultsMap = createOrgPermissionConfig?.role_defaults;
      if (roleDefaultsMap && typeof roleDefaultsMap === "object") {
        // Try multiple key formats
        const mapped = roleDefaultsMap[formData.role]
          || roleDefaultsMap[selectedRoleKey]
          || roleDefaultsMap[String(formData.role).toUpperCase()];
        defaults = toStringArray(mapped);
      }
    }

    // Fallback: if still empty, use entire grantable set
    if (defaults.length === 0) {
      defaults = [...createGrantableSet];
    }

    // Filter to only permissions available in this org type
    return defaults.filter((perm) => createGrantableSet.has(perm));
  }, [createGrantableSet, createOrgPermissionConfig, formData.role]);

  const createPermissionsDirty = useMemo(() => {
    const left = new Set(createSelectedPermissions);
    const right = new Set(createBaselinePermissions);
    if (left.size !== right.size) return true;
    for (const item of left) {
      if (!right.has(item)) return true;
    }
    return false;
  }, [createBaselinePermissions, createSelectedPermissions]);

  useEffect(() => {
    if (!formRoleOptions.includes(formData.role)) {
      setFormData((prev) => ({ ...prev, role: formRoleOptions[0] || "super_admin" }));
    }
  }, [formData.role, formRoleOptions]);

  useEffect(() => {
    if (!openAddUserModal || !canManagePermissions) return;

    let cancelled = false;
    setCreatePermissionsLoading(true);
    setCreatePermissionsError("");

    fetchRbacCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setCreatePermissionsCatalog(catalog || { permissions: [], org_types: [], fixed_capabilities: [] });
      })
      .catch((err) => {
        if (cancelled) return;
        const { message } = extractApiError(err);
        setCreatePermissionsError(message || "Failed to load permission matrix.");
        setCreatePermissionsCatalog({ permissions: [], org_types: [], fixed_capabilities: [] });
      })
      .finally(() => {
        if (!cancelled) setCreatePermissionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canManagePermissions, openAddUserModal]);

  useEffect(() => {
    if (!openAddUserModal || !canManagePermissions) return;
    setCreateBaselinePermissions(createRoleDefaults);
    setCreateSelectedPermissions(createRoleDefaults);
  }, [canManagePermissions, createOrgType, createRoleDefaults, formData.role, openAddUserModal]);

  const fetchOrganizations = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const { data } = await api.get("/organizations", {
        params: { is_active: true, page_size: 100 },
      });
      setOrganizations(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load organizations.");
      setOrganizations([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (q.trim()) params.search = q.trim();
      if (roleFilter !== "all") params.role = roleFilter;
      if (activeFilter !== "all") params.is_active = activeFilter === "true";
      if (orgIdFilter !== "all") params.org_id = orgIdFilter;

      const { data } = await api.get("/users", { params });
      setUsers(Array.isArray(data?.data) ? data.data : []);
      setMeta(
        data?.meta || {
          total: 0,
          page,
          page_size: pageSize,
          pages: 1,
        }
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load users.");
      setUsers([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [q, roleFilter, activeFilter, orgIdFilter, page, pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetFilters = () => {
    setQ("");
    setRoleFilter("all");
    setActiveFilter("all");
    setOrgIdFilter("all");
    setPage(1);
  };

  const activeFiltersCount = [
    roleFilter !== "all",
    activeFilter !== "all",
    orgIdFilter !== "all",
  ].filter(Boolean).length;

  const hasAnyFilter = !!q.trim() || activeFiltersCount > 0;

  const onChangeSearch = (value) => {
    setQ(value);
    setPage(1);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setCreateFieldErrors((prev) => {
      if (!prev || !prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const handleSelectChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    setCreateFieldErrors((prev) => {
      if (!prev || !prev[fieldName]) return prev;
      const copy = { ...prev };
      delete copy[fieldName];
      return copy;
    });
  };

  const handleCreatePermissionToggle = (permissionKey, checked) => {
    if (!createGrantableSet.has(permissionKey)) return;
    setCreateSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (checked) next.add(permissionKey);
      else next.delete(permissionKey);
      return [...next].sort();
    });
  };

  const handleResetForm = () => {
    setFormData({
      organization_id: "",
      first_name: "",
      last_name: "",
      email: "",
      username: "",
      phone_number: "",
      password: "",
      role: "super_admin",
      state: "",
      district: "",
      city: "",
      pincode: "",
      address: "",
      blood_group: "",
      emergency_contact: "",
    });
    setCreateFieldErrors({});
    setCreateBaselinePermissions([]);
    setCreateSelectedPermissions([]);
    setCreatePermissionsError("");
  };

  const handleOpenModal = () => {
    handleResetForm();
    setSubmitError("");
    setCreateFieldErrors({});
    if (!isPlatformAdmin && callerOrgId) {
      setFormData((prev) => ({ ...prev, organization_id: callerOrgId }));
    }
    setOpenAddUserModal(true);
  };

  const handleCloseModal = () => {
    setOpenAddUserModal(false);
    handleResetForm();
    setSubmitError("");
  };

  const handleCreateUser = async () => {
    const resolvedOrgId = isPlatformAdmin
      ? (formData.organization_id || null)
      : (callerOrgId || formData.organization_id || null);

    if (!isPlatformAdmin && !resolvedOrgId) {
      setSubmitError("Your account is not mapped to an organization. Contact platform admin.");
      return;
    }

    const targetOrganization = resolvedOrgId
      ? organizations.find((org) => org.id === resolvedOrgId)
      : null;

    if (resolvedOrgId && !targetOrganization && isPlatformAdmin) {
      setSubmitError("Please select a valid organization.");
      return;
    }

    if (targetOrganization?.org_type === "CUSTOMER") {
      setSubmitError("Customer accounts must be created through onboarding, not the staff user form.");
      return;
    }

    if (!formData.email.trim()) {
      setSubmitError("Email is required");
      return;
    }
    if (!formData.username.trim()) {
      setSubmitError("Username is required");
      return;
    }
    if (!formData.password.trim()) {
      setSubmitError("Password is required");
      return;
    }
    if (formData.password.trim().length < 8) {
      setSubmitError("Password must be at least 8 characters.");
      return;
    }
    if (!formData.role) {
      setSubmitError("Role is required");
      return;
    }
    if (formData.role === "owner") {
      setSubmitError("Owner accounts cannot be created here.");
      return;
    }
    const effectiveOrgType = targetOrganization?.org_type || (!isPlatformAdmin ? user?.org_type : selectedOrganization?.org_type);
    const scopedRoleOptions = effectiveOrgType
      ? (roleOptionsByOrgType[effectiveOrgType] || ["org_admin", "operator"])
      : formRoleOptions;
    if (!scopedRoleOptions.includes(formData.role)) {
      setSubmitError(`Role ${formData.role} is not valid for ${effectiveOrgType || "the selected organization"}`);
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const createUserData = {
        organization_id: isPlatformAdmin ? (resolvedOrgId || null) : null,
        email: formData.email.trim(),
        username: formData.username.trim(),
        password: formData.password.trim(),
        role: formData.role,
        first_name: formData.first_name.trim() || null,
        last_name: formData.last_name.trim() || null,
        phone_number: formData.phone_number.trim() || null,
        state: formData.state.trim() || null,
        city: formData.city.trim() || null,
        district: formData.district.trim() || null,
        pincode: formData.pincode.trim() || null,
        address: formData.address.trim() || null,
        blood_group: formData.blood_group || null,
        emergency_contact: formData.emergency_contact.trim() || null,
        must_change_password: true,
      };

      const createResponse = await api.post("/users", createUserData);
      const createdUser = unwrapSingle(createResponse) || createResponse?.data || null;
      const createdUserId = createdUser?.id || createdUser?.user_id || createdUser?.userId || null;

      if (
        canManagePermissions
        && createdUserId
        && createOrgPermissionConfig?.customizable !== false
      ) {
        try {
          const nextPermissions = [...new Set(createSelectedPermissions)]
            .filter((perm) => createGrantableSet.has(perm))
            .sort();

          await api.put(`/users/${createdUserId}/permissions`, {
            permissions: nextPermissions,
          });
        } catch (permErr) {
          const { message } = extractApiError(permErr);
          toast.error(message || "User created, but permission overrides could not be saved.");
        }
      }

      await fetchUsers();
      handleCloseModal();
      toast.success("User created successfully.");
    } catch (err) {
      const statusCode = err?.response?.status;
      const { message: backendMessage, details, code } = extractApiError(err);

      // Map field-level validation errors to form inputs
      if (details?.fields && Array.isArray(details.fields)) {
        const fieldMap = {};
        details.fields.forEach((f) => {
          if (f?.field) fieldMap[f.field] = f.message || f.type || "Invalid value";
        });
        setCreateFieldErrors(fieldMap);
      }

      const errorMessage =
        statusCode === 409
          ? backendMessage || "A user with this email or username already exists."
          : backendMessage || err?.message || "Failed to create user. Please try again.";
      setSubmitError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHeader
        title="Users"
        subtitle="Drivers, fleet owners and platform users."
        actions={
          canCreateUsers ? (
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="add-user-btn"
              onClick={handleOpenModal}
            >
              <Plus size={14} className="mr-1.5" /> Add user
            </Button>
          ) : null
        }
      />

      {/* Filters Section */}
      <div className="flex flex-col gap-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => onChangeSearch(e.target.value)}
              placeholder="Search by name, email, username, phone"
              className="pl-10 h-11 border-slate-200 bg-slate-50/50"
              data-testid="users-search"
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-11 gap-2 border-slate-200 bg-white hover:bg-slate-50">
                <Filter size={14} className="text-slate-400" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
                    {activeFiltersCount}
                  </span>
                )}
                <ChevronDown size={14} className="text-slate-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-4" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <h4 className="text-sm font-bold text-slate-900">Filter Parameters</h4>
                  {activeFiltersCount > 0 && (
                    <button onClick={resetFilters} className="text-[11px] font-bold text-blue-600 hover:underline">
                      Reset All
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Organization</label>
                    <Select
                      value={orgIdFilter}
                      onValueChange={(value) => {
                        setOrgIdFilter(value);
                        setPage(1);
                      }}
                      disabled={loadingOrgs}
                    >
                      <SelectTrigger className="h-10 bg-slate-50/50 border-slate-200">
                        <div className="flex items-center gap-2">
                          <Building2 size={13} className="text-slate-400" />
                          <SelectValue placeholder={loadingOrgs ? "Loading orgs..." : "All Organizations"} />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Organizations</SelectItem>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Role</label>
                    <Select
                      value={roleFilter}
                      onValueChange={(value) => {
                        setRoleFilter(value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="h-10 bg-slate-50/50 border-slate-200">
                        <div className="flex items-center gap-2">
                          <UserCog size={13} className="text-slate-400" />
                          <SelectValue placeholder="All Roles" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        {roleFilterOptions.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Active Status</label>
                    <Select
                      value={activeFilter}
                      onValueChange={(value) => {
                        setActiveFilter(value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="h-10 bg-slate-50/50 border-slate-200">
                        <div className="flex items-center gap-2">
                          <Filter size={13} className="text-slate-400" />
                          <SelectValue placeholder="All" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">Page Size</label>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(value) => {
                        setPageSize(Number(value));
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="h-10 bg-slate-50/50 border-slate-200">
                        <div className="flex items-center gap-2">
                          <SelectValue placeholder="Page size" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {hasAnyFilter && (
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="h-11 px-3 text-slate-400 hover:text-rose-500 transition-colors"
              title="Clear all filters"
            >
              <X size={16} />
            </Button>
          )}

          <Button
            variant="outline"
            onClick={fetchUsers}
            className="h-11 gap-2 border-slate-200 bg-white hover:bg-slate-50"
            title="Refresh users"
          >
            <RefreshCw size={14} />
            Refresh
          </Button>
        </div>
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-medium text-slate-500">
            {loading ? "Loading users..." : `${meta.total} users found`}
          </span>
          <span className="text-xs text-slate-500">
            Page {meta.page || page} of {meta.pages || 1}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div >
      <SectionCard padded={false} className="fix-data-table">
        <DataTable
        
          data={users}
          rowKey="id"
          onRowClick={(u) => handleViewUser(u.id)}
          loading={loading}
          loadingMessage="Loading users..."
          emptyState={
            <div className="flex flex-col items-center gap-2 px-5 py-12 text-center text-slate-500">
              <Search size={32} className="text-slate-200" />
              <p>No users found matching your search or filters.</p>
              <button onClick={resetFilters} className="text-sm text-blue-600 font-bold hover:underline">
                Clear all filters
              </button>
            </div>
          }
          headClassName="font-bold"
          headerCellClassName="py-4"
          defaultCellClassName="py-4"
          rowClassName="text-sm transition-colors hover:bg-slate-50/50"
          bodyClassName="divide-y divide-slate-100"
          columns={[
            {
              key: "email", label: "Email",
              cellClassName: "font-mono text-xs font-bold text-slate-900",
              render: (u) => (
                u.email
                  ? <span title={u.email} className="font-mono text-xs font-bold text-slate-900">{truncateWords(u.email, 8)}</span>
                  : "N/A"
              ),
            },
            {
              key: "name", label: "Name",
              cellClassName: "font-semibold text-slate-900",
              render: (u) => (
                (u.first_name || "").trim() || (u.last_name || "").trim()
                  ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                  : "-"
              ),
            },
            { key: "contact", label: "Contact", cellClassName: "text-slate-600", render: (u) => u.phone_number || "NA" },
            { key: "role", label: "Role", cellClassName: "text-slate-700 font-medium" },
            {
              key: "organization", label: "Organization",
              render: (u) => (
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-900">{u.org_name || "-"}</span>
                </div>
              ),
            },
            {
              key: "location", label: "Location",
              render: (u) => (
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5 text-slate-900 font-medium">
                    <MapPin size={12} className="text-slate-400" />
                    <span className="text-[13px]">{u.city || "-"}</span>
                  </div>
                  <span className="text-[11px] text-slate-400 ml-4.5 uppercase tracking-wider font-bold pl-3">{u.state || "-"}</span>
                </div>
              ),
            },
            {
              key: "status", label: "Status",
              render: (u) => (
                <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-[11px] font-bold ${
                  u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                }`}>
                  {u.is_active ? "Active" : "Inactive"}
                </span>
              ),
            },
            { key: "last-login", label: "Last Login", cellClassName: "text-slate-600", render: (u) => formatDateTime(u.last_login_at) },
            {
              key: "actions", label: "Actions", className: "text-right", cellClassName: "text-right",
              render: (u) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      data-row-click-ignore="true"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" data-row-click-ignore="true" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      data-row-click-ignore="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewUser(u.id);
                      }}
                    >
                      <Eye size={14} /> View
                    </DropdownMenuItem>
                    {canUpdateUsers ? (
                      <DropdownMenuItem
                        data-row-click-ignore="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(u.id);
                        }}
                      >
                        <Pencil size={14} /> Edit
                      </DropdownMenuItem>
                    ) : null}
                    {canDeleteUsers ? (
                      <DropdownMenuItem
                        data-row-click-ignore="true"
                        className="text-rose-600 focus:text-rose-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({
                            id: u.id,
                            name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.email || "this user",
                            isActive: u.is_active,
                          });
                        }}
                      >
                        <Trash2 size={14} /> Delete
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-4">
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page <= 1}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            className="border-slate-200"
            disabled={loading || page >= (meta.pages || 1)}
            onClick={() => setPage((prev) => Math.min(meta.pages || 1, prev + 1))}
          >
            Next
          </Button>
        </div>

      </SectionCard>
      </div>

      <Dialog open={openAddUserModal} onOpenChange={(open) => (open ? setOpenAddUserModal(true) : handleCloseModal())}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Register User</DialogTitle>
            <DialogDescription className="font-bold text-slate-500">
              Add a new user to the system.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="h-auto gap-2 rounded-2xl bg-slate-100 p-2">
                <TabsTrigger value="details" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Details</TabsTrigger>
                {canManagePermissions && (
                  <TabsTrigger value="permissions" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Permissions</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="details" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  First Name (optional)
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  placeholder="e.g. John"
                  value={formData.first_name}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.first_name && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.first_name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Last Name (optional)
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  placeholder="e.g. Doe"
                  value={formData.last_name}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.last_name && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.last_name}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization_id" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Organization {!isPlatformAdmin ? "(auto from your account)" : "(optional)"}
                </Label>
                <Select value={formData.organization_id} onValueChange={(value) => handleSelectChange("organization_id", value)}>
                  <SelectTrigger className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" disabled={!isPlatformAdmin}>
                    <SelectValue placeholder={loadingOrgs ? "Loading organizations..." : "Select organization (optional)"} />
                  </SelectTrigger>
                  <SelectContent>
                    {addUserOrganizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name} ({org.org_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {createFieldErrors.organization_id && (
                  <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.organization_id}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="role" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Role *
                </Label>
                <Select value={formData.role} onValueChange={(value) => handleSelectChange("role", value)}>
                  <SelectTrigger className="h-12 rounded-2xl bg-slate-50/50 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formRoleOptions.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {createFieldErrors.role && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.role}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="e.g. john.doe@email.com"
                  value={formData.email}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.email && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.email}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Username *
                </Label>
                <Input
                  id="username"
                  name="username"
                  placeholder="e.g. john.doe"
                  value={formData.username}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.username && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.username}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Password *
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.password && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.password}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone_number" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Phone Number (optional)
                </Label>
                <Input
                  id="phone_number"
                  name="phone_number"
                  placeholder="e.g. +91 9876543210"
                  value={formData.phone_number}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.phone_number && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.phone_number}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  State (optional)
                </Label>
                <Input
                  id="state"
                  name="state"
                  placeholder="e.g. Maharashtra"
                  value={formData.state}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.state && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.state}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="district" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  District (optional)
                </Label>
                <Input
                  id="district"
                  name="district"
                  placeholder="e.g. Pune"
                  value={formData.district}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.district && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.district}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  City (optional)
                </Label>
                <Input
                  id="city"
                  name="city"
                  placeholder="e.g. Pune"
                  value={formData.city}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.city && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.city}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="pincode" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Pincode (optional)
                </Label>
                <Input
                  id="pincode"
                  name="pincode"
                  placeholder="e.g. 411001"
                  value={formData.pincode}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.pincode && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.pincode}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                Address (optional)
              </Label>
              <Input
                id="address"
                name="address"
                placeholder="Enter full address"
                value={formData.address}
                onChange={handleFormChange}
                className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
              />
              {createFieldErrors.address && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="blood_group" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Blood Group (optional)
                </Label>
                <Select value={formData.blood_group || "none"} onValueChange={(value) => handleSelectChange("blood_group", value === "none" ? "" : value)}>
                  <SelectTrigger className="h-12 rounded-2xl bg-slate-50/50 border-slate-200">
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
                {createFieldErrors.blood_group && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.blood_group}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Emergency Contact (optional)
                </Label>
                <Input
                  id="emergency_contact"
                  name="emergency_contact"
                  placeholder="e.g. +91 9123456789"
                  value={formData.emergency_contact}
                  onChange={handleFormChange}
                  className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {createFieldErrors.emergency_contact && <p className="text-xs text-rose-600 mt-1 ml-1">{createFieldErrors.emergency_contact}</p>}
              </div>
            </div>

              </TabsContent>

              {canManagePermissions && (
                <TabsContent value="permissions" className="mt-4" forceMount>
                  <PermissionsEditor
                    isCreate
                    orgType={createOrgType}
                    roleDefaults={createRoleDefaults}
                    grantableSet={createGrantableSet}
                    selectedPermissions={createSelectedPermissions}
                    onPermissionChange={setCreateSelectedPermissions}
                  />
                </TabsContent>
              )}
            </Tabs>

            {submitError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {submitError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-slate-900 text-white hover:bg-slate-800"
                onClick={handleCreateUser}
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Create User"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* ── Edit User Dialog ──────────────────────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditError(""); } }}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Edit User</DialogTitle>
            <DialogDescription className="font-bold text-slate-500">
              {editOrgName ? `${editOrgName} · ${editOrgType?.replace(/_/g, " ")}` : "Update user profile and access settings."}
            </DialogDescription>
          </DialogHeader>

          {editLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-slate-400" />
            </div>
          )}

          {!editLoading && (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="h-auto gap-2 rounded-2xl bg-slate-100 p-2">
                <TabsTrigger value="details" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Details</TabsTrigger>
                {canManagePermissions && (
                  <TabsTrigger value="permissions" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Permissions</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="details" className="mt-3 space-y-1.5">
            <div className="grid grid-cols-2 gap-1.5">
              <div className="space-y-1">
                <Label htmlFor="edit_first_name" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  First Name (optional)
                </Label>
                <Input
                  id="edit_first_name"
                  name="first_name"
                  placeholder="e.g. John"
                  value={editFormData.first_name}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.first_name && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.first_name}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_last_name" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Last Name (optional)
                </Label>
                <Input
                  id="edit_last_name"
                  name="last_name"
                  placeholder="e.g. Doe"
                  value={editFormData.last_name}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.last_name && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.last_name}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              <div className="space-y-1">
                <Label htmlFor="edit_organization" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Organization (auto from your account)
                </Label>
                <div className="flex h-10 items-center rounded-2xl border border-slate-200 bg-slate-100/60 px-4 text-sm text-slate-500 font-medium">
                  {editOrgName || "—"}
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_role" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Role *
                </Label>
                <Select value={editFormData.role} onValueChange={(value) => handleEditSelectChange("role", value)}>
                  <SelectTrigger className="h-10 rounded-2xl bg-slate-50/50 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {editRoleOptions.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editFieldErrors.role && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.role}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              <div className="space-y-1">
                <Label htmlFor="edit_is_active" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Account Status
                </Label>
                <Select
                  value={editFormData.is_active ? "true" : "false"}
                  onValueChange={(v) => handleEditSelectChange("is_active", v === "true")}
                >
                  <SelectTrigger className="h-10 rounded-2xl bg-slate-50/50 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {editFieldErrors.is_active && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.is_active}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_phone_number" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Phone Number (optional)
                </Label>
                <Input
                  id="edit_phone_number"
                  name="phone_number"
                  placeholder="e.g. +91 9876543210"
                  value={editFormData.phone_number}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.phone_number && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.phone_number}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              <div className="space-y-1">
                <Label htmlFor="edit_state" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  State (optional)
                </Label>
                <Input
                  id="edit_state"
                  name="state"
                  placeholder="e.g. Maharashtra"
                  value={editFormData.state}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.state && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.state}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_district" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  District (optional)
                </Label>
                <Input
                  id="edit_district"
                  name="district"
                  placeholder="e.g. Pune"
                  value={editFormData.district}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.district && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.district}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              <div className="space-y-1">
                <Label htmlFor="edit_city" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  City (optional)
                </Label>
                <Input
                  id="edit_city"
                  name="city"
                  placeholder="e.g. Pune"
                  value={editFormData.city}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.city && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.city}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_pincode" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Pincode (optional)
                </Label>
                <Input
                  id="edit_pincode"
                  name="pincode"
                  placeholder="e.g. 411001"
                  value={editFormData.pincode}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.pincode && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.pincode}</p>}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit_address" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                Address (optional)
              </Label>
              <Input
                id="edit_address"
                name="address"
                placeholder="Enter full address"
                value={editFormData.address}
                onChange={handleEditFormChange}
                className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
              />
              {editFieldErrors.address && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              <div className="space-y-1">
                <Label htmlFor="edit_blood_group" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Blood Group (optional)
                </Label>
                <Select value={editFormData.blood_group || "none"} onValueChange={(value) => handleEditSelectChange("blood_group", value === "none" ? "" : value)}>
                  <SelectTrigger className="h-10 rounded-2xl bg-slate-50/50 border-slate-200">
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
                {editFieldErrors.blood_group && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.blood_group}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit_emergency_contact" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                  Emergency Contact (optional)
                </Label>
                <Input
                  id="edit_emergency_contact"
                  name="emergency_contact"
                  placeholder="e.g. +91 9123456789"
                  value={editFormData.emergency_contact}
                  onChange={handleEditFormChange}
                  className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
                />
                {editFieldErrors.emergency_contact && <p className="text-xs text-rose-600 mt-1 ml-1">{editFieldErrors.emergency_contact}</p>}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit_password" className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">
                New Password (optional)
              </Label>
              <Input
                id="edit_password"
                name="password"
                type="password"
                placeholder="Leave blank to keep current password"
                value={editFormData.password}
                onChange={handleEditFormChange}
                className="h-10 rounded-2xl bg-slate-50/50 border-slate-200"
              />
            </div>

              {editError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {editError}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  onClick={handleUpdateUser}
                  disabled={editSubmitting}
                >
                  {editSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
              </TabsContent>

              {canManagePermissions && (
                <TabsContent value="permissions" className="mt-4" forceMount>
                  <PermissionsEditor
                    selectedPermissions={editSelectedPermissions}
                    onPermissionChange={setEditSelectedPermissions}
                    key={permissionsRefreshKey}
                    userId={editUserId}
                    orgType={editOrgType}
                    currentUserId={user?.user_id}
                  />
                </TabsContent>
              )}
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Role Change Confirmation Dialog ──────────────────────────────── */}
      <ConfirmDialog
        open={roleChangeConfirmPending}
        onOpenChange={(o) => { if (!o) setRoleChangeConfirmPending(false); }}
        title="Discard permission overrides?"
        description="Changing this user's role will discard any custom permission overrides they have. Continue?"
        confirmLabel="Yes, change role"
        onConfirm={() => submitUserUpdate()}
      />

      {/* ── Delete Confirmation Dialog ──────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o && !deleteSubmitting) setDeleteTarget(null); }}
        title="Remove user?"
        description={
          <>
            You are about to remove{" "}
            <span className="font-bold text-slate-900">{deleteTarget?.name}</span>.
            {" "}This will deactivate their account and revoke all access. The record will be preserved.
          </>
        }
        confirmLabel="Yes, remove user"
        variant="danger"
        loading={deleteSubmitting}
        onConfirm={handleDeleteUser}
      />

      {/* ── View User Dialog ──────────────────────────────────────────── */}
      <Dialog open={viewOpen} onOpenChange={(o) => { if (!o) { setViewOpen(false); setViewUser(null); setViewError(""); } }}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">
              {viewLoading ? "Loading…" : viewUser ? `${viewUser.first_name || ""} ${viewUser.last_name || ""}`.trim() || viewUser.username : "User Details"}
            </DialogTitle>
            <DialogDescription className="font-bold text-slate-500">
              {viewUser ? `${viewUser.username} · ${viewUser.email}` : "Fetching user details from the server."}
            </DialogDescription>
          </DialogHeader>

          {/* Loading */}
          {viewLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-slate-400" />
            </div>
          )}

          {/* Error */}
          {!viewLoading && viewError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {viewError}
            </div>
          )}

          {/* Content */}
          {!viewLoading && viewUser && (() => {
            const Field = ({ label, value, mono = false }) => (
              <div className="space-y-1.5">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{label}</p>
                <div className={`flex h-12 items-center rounded-2xl border border-slate-200 bg-slate-50/50 px-4 text-sm ${
                  mono ? "font-mono" : ""
                } ${value ? "text-slate-900" : "text-slate-400 italic"}`}>
                  {value || "—"}
                </div>
              </div>
            );

            return (
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="h-auto gap-2 rounded-2xl bg-slate-100 p-2">
                  <TabsTrigger value="details" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Details</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4">
                  <div className="space-y-5 py-2">
                    {/* Status badges */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                        viewUser.is_active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-500 ring-1 ring-slate-200"
                      }`}>
                        {viewUser.is_active ? "● Active" : "○ Inactive"}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">
                        {viewUser.role?.replace(/_/g, " ")}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200">
                        {viewUser.org_type?.replace(/_/g, " ")}
                      </span>
                      {viewUser.must_change_password && (
                        <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200">
                          ⚠ Must change password
                        </span>
                      )}
                    </div>

                    {/* Personal */}
                    <div>
                      <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">Personal Information</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="First Name" value={viewUser.first_name} />
                        <Field label="Last Name" value={viewUser.last_name} />
                        <Field label="Email" value={viewUser.email} />
                        <Field label="Username" value={viewUser.username} mono />
                        <Field label="Phone Number" value={viewUser.phone_number} mono />
                        <Field label="Blood Group" value={viewUser.blood_group} />
                        <div className="col-span-2">
                          <Field label="Emergency Contact" value={viewUser.emergency_contact} mono />
                        </div>
                      </div>
                    </div>

                    {/* Organisation */}
                    <div>
                      <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">Organisation</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Organisation" value={viewUser.org_name} />
                        <Field label="Type" value={viewUser.org_type?.replace(/_/g, " ")} />
                      </div>
                    </div>

                    {/* Location */}
                    <div>
                      <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">Location</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="State" value={viewUser.state} />
                        <Field label="District" value={viewUser.district} />
                        <Field label="City" value={viewUser.city} />
                        <Field label="Pincode" value={viewUser.pincode} mono />
                        <div className="col-span-2">
                          <Field label="Address" value={viewUser.address} />
                        </div>
                      </div>
                    </div>

                    {/* Activity */}
                    <div>
                      <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">Activity</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Last Login" value={formatDateTime(viewUser.last_login_at)} />
                        <Field label="Created At" value={formatDateTime(viewUser.created_at)} />
                        <Field label="Updated At" value={formatDateTime(viewUser.updated_at)} />
                      </div>
                    </div>

                    {/* Permissions */}
                    {Array.isArray(viewUser.permissions) && viewUser.permissions.length > 0 && (
                      <div>
                        <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">
                          Permissions ({viewUser.permissions.length})
                        </p>
                        <div className="flex flex-wrap gap-1.5 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
                          {viewUser.permissions.map((perm) => (
                            <span
                              key={perm}
                              className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-[11px] font-bold text-slate-700 ring-1 ring-slate-200"
                            >
                              <Shield size={10} className="text-slate-400" />
                              {perm.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end pt-1">
                      <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
                    </div>
                  </div>
                </TabsContent>

              </Tabs>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

